import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  FileText, ChevronLeft, Download, Printer, PenTool, Check,
  Loader2, Building, Truck, User, MapPin, Calendar, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Signature Pad Component
const SignaturePad = ({ onSave, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0c10';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#4ADE80';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSigned(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && hasSigned) {
      const canvas = canvasRef.current;
      onSave?.(canvas.toDataURL('image/png'));
    }
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0c10';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
    onSave?.(null);
  };

  if (existingSignature) {
    return (
      <div className="space-y-2">
        <img
          src={existingSignature.signature_data}
          alt="Signature"
          className="w-full h-24 object-contain border border-[#1a1d24] rounded-lg bg-[#0a0c10]"
        />
        <p className="text-xs text-gray-400">
          Signed by {existingSignature.signer_name} on {new Date(existingSignature.signed_at).toLocaleString()}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={280}
        height={100}
        className="border border-[#1a1d24] rounded-lg w-full touch-none"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <Button type="button" variant="outline" size="sm" onClick={clear} className="w-full">
        Clear Signature
      </Button>
    </div>
  );
};

const BOLPage = () => {
  const { loadId } = useParams();
  const navigate = useNavigate();
  
  const [bol, setBol] = useState(null);
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    driver_name: '',
    driver_phone: '',
    truck_number: '',
    trailer_number: '',
    special_instructions: '',
    notes: '',
  });
  
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState('carrier');

  useEffect(() => {
    loadBOL();
  }, [loadId]);

  const loadBOL = async () => {
    try {
      // Try to get existing BOL
      const response = await api.get(`/bols/load/${loadId}`);
      setBol(response.data);
      setLoad(response.data.load);
      setFormData({
        driver_name: response.data.driver_name || '',
        driver_phone: response.data.driver_phone || '',
        truck_number: response.data.truck_number || '',
        trailer_number: response.data.trailer_number || '',
        special_instructions: response.data.special_instructions || '',
        notes: response.data.notes || '',
      });
    } catch (error) {
      if (error.response?.status === 404) {
        // BOL doesn't exist, create one
        try {
          const createResponse = await api.post('/bols', { load_id: loadId });
          setBol(createResponse.data);
          // Get load details
          const loadResponse = await api.get(`/loads/${loadId}`);
          setLoad(loadResponse.data);
        } catch (createError) {
          toast.error('Failed to create BOL');
        }
      } else {
        toast.error('Failed to load BOL');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/bols/${bol.id}`, formData);
      toast.success('BOL updated');
      setEditMode(false);
      loadBOL();
    } catch (error) {
      toast.error('Failed to save BOL');
    } finally {
      setSaving(false);
    }
  };

  const handleSign = async () => {
    if (!signatureData) {
      toast.error('Please provide a signature');
      return;
    }
    if (!signerName) {
      toast.error('Please enter signer name');
      return;
    }

    setSigning(true);
    try {
      await api.post(`/bols/${bol.id}/sign`, {
        signature_data: signatureData,
        signer_name: signerName,
        signer_role: signerRole,
      });
      toast.success('BOL signed successfully');
      loadBOL();
    } catch (error) {
      toast.error('Failed to sign BOL');
    } finally {
      setSigning(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16 pb-24 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6 print:hidden">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">Bill of Lading</h1>
            <p className="text-sm text-[#4ADE80]">{bol?.bol_number}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>

        {/* BOL Document */}
        <div className="bg-white text-black rounded-lg overflow-hidden print:shadow-none">
          {/* BOL Header */}
          <div className="bg-[#0a0c10] text-white p-6 print:bg-black">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold tracking-wider">HAULLOGIX</h2>
                <p className="text-[#4ADE80] text-sm">Bill of Lading</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-lg">{bol?.bol_number}</p>
                <p className="text-xs text-gray-400">
                  Created: {new Date(bol?.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Shipper & Receiver Info */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Shipper */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <Building className="w-4 h-4" />
                  <span className="font-semibold text-sm uppercase">Shipper</span>
                </div>
                <p className="font-medium">{bol?.shipper_name || 'N/A'}</p>
                <p className="text-sm text-gray-600">{bol?.shipper_company}</p>
                <p className="text-sm text-gray-600">{bol?.shipper_address}</p>
                <p className="text-sm text-gray-600">{bol?.shipper_phone}</p>
              </div>

              {/* Receiver */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <User className="w-4 h-4" />
                  <span className="font-semibold text-sm uppercase">Receiver</span>
                </div>
                <p className="font-medium">{bol?.receiver_name || 'N/A'}</p>
                <p className="text-sm text-gray-600">{bol?.receiver_company}</p>
                <p className="text-sm text-gray-600">{bol?.receiver_address}</p>
                <p className="text-sm text-gray-600">{bol?.receiver_phone}</p>
              </div>
            </div>

            {/* Carrier Info */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-gray-600">
                  <Truck className="w-4 h-4" />
                  <span className="font-semibold text-sm uppercase">Carrier Information</span>
                </div>
                {!editMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    className="print:hidden"
                  >
                    Edit
                  </Button>
                )}
              </div>

              {editMode ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Driver Name</Label>
                    <Input
                      value={formData.driver_name}
                      onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Driver Phone</Label>
                    <Input
                      value={formData.driver_phone}
                      onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Truck Number</Label>
                    <Input
                      value={formData.truck_number}
                      onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Trailer Number</Label>
                    <Input
                      value={formData.trailer_number}
                      onChange={(e) => setFormData({ ...formData, trailer_number: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Special Instructions</Label>
                    <Textarea
                      value={formData.special_instructions}
                      onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="md:col-span-2 flex gap-2">
                    <Button variant="outline" onClick={() => setEditMode(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#4ADE80] text-white">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Company:</span>
                    <span className="ml-2 font-medium">{bol?.carrier_company || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">MC#:</span>
                    <span className="ml-2 font-medium">{bol?.carrier_mc_number || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Driver:</span>
                    <span className="ml-2 font-medium">{bol?.driver_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <span className="ml-2 font-medium">{bol?.driver_phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Truck#:</span>
                    <span className="ml-2 font-medium">{bol?.truck_number || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Trailer#:</span>
                    <span className="ml-2 font-medium">{bol?.trailer_number || 'N/A'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Vehicle Info */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-3">
                <FileText className="w-4 h-4" />
                <span className="font-semibold text-sm uppercase">Vehicle(s)</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-gray-500">#</th>
                    <th className="text-left py-2 text-gray-500">Year/Make/Model</th>
                    <th className="text-left py-2 text-gray-500">VIN</th>
                    <th className="text-left py-2 text-gray-500">Condition</th>
                  </tr>
                </thead>
                <tbody>
                  {bol?.vehicles?.map((vehicle, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2">{index + 1}</td>
                      <td className="py-2">{vehicle.year} {vehicle.make_model}</td>
                      <td className="py-2 font-mono text-xs">{vehicle.vin || 'N/A'}</td>
                      <td className="py-2">{vehicle.condition || 'See inspection'}</td>
                    </tr>
                  )) || (
                    <tr className="border-b">
                      <td className="py-2">1</td>
                      <td className="py-2">{load?.vehicle_year} {load?.vehicle_make_model}</td>
                      <td className="py-2 font-mono text-xs">N/A</td>
                      <td className="py-2">See inspection</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Signatures */}
            <div className="grid md:grid-cols-3 gap-4 print:break-inside-avoid">
              {/* Shipper Signature */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <PenTool className="w-4 h-4" />
                  <span className="font-semibold text-sm uppercase">Shipper</span>
                </div>
                {bol?.shipper_signature ? (
                  <div>
                    <img
                      src={bol.shipper_signature.signature_data}
                      alt="Shipper signature"
                      className="h-16 object-contain"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {bol.shipper_signature.signer_name}
                    </p>
                  </div>
                ) : (
                  <div className="h-16 border-b border-dashed border-gray-300 flex items-end justify-center pb-1">
                    <span className="text-xs text-gray-400">Awaiting signature</span>
                  </div>
                )}
              </div>

              {/* Carrier Signature */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <PenTool className="w-4 h-4" />
                  <span className="font-semibold text-sm uppercase">Carrier</span>
                </div>
                {bol?.carrier_signature ? (
                  <div>
                    <img
                      src={bol.carrier_signature.signature_data}
                      alt="Carrier signature"
                      className="h-16 object-contain"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {bol.carrier_signature.signer_name}
                    </p>
                  </div>
                ) : (
                  <div className="h-16 border-b border-dashed border-gray-300 flex items-end justify-center pb-1">
                    <span className="text-xs text-gray-400">Awaiting signature</span>
                  </div>
                )}
              </div>

              {/* Receiver Signature */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <PenTool className="w-4 h-4" />
                  <span className="font-semibold text-sm uppercase">Receiver</span>
                </div>
                {bol?.receiver_signature ? (
                  <div>
                    <img
                      src={bol.receiver_signature.signature_data}
                      alt="Receiver signature"
                      className="h-16 object-contain"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {bol.receiver_signature.signer_name}
                    </p>
                  </div>
                ) : (
                  <div className="h-16 border-b border-dashed border-gray-300 flex items-end justify-center pb-1">
                    <span className="text-xs text-gray-400">Awaiting signature</span>
                  </div>
                )}
              </div>
            </div>

            {/* Special Instructions */}
            {bol?.special_instructions && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-semibold text-sm uppercase">Special Instructions</span>
                </div>
                <p className="text-sm">{bol.special_instructions}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sign BOL Section */}
        <Card className="mt-6 bg-[#12151a] border-[#1a1d24] print:hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <PenTool className="w-5 h-5 text-[#4ADE80]" />
              Sign Bill of Lading
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Your Name</Label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  className="bg-[#0a0c10] border-[#1a1d24] text-white"
                />
              </div>
              <div>
                <Label className="text-white">Signing As</Label>
                <select
                  value={signerRole}
                  onChange={(e) => setSignerRole(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-[#0a0c10] border border-[#1a1d24] text-white"
                >
                  <option value="shipper">Shipper</option>
                  <option value="carrier">Carrier/Driver</option>
                  <option value="receiver">Receiver</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Signature</Label>
              <div className="bg-[#0a0c10] rounded-lg p-3">
                <SignaturePad onSave={setSignatureData} />
              </div>
            </div>

            <Button
              onClick={handleSign}
              disabled={signing || !signatureData || !signerName}
              className="w-full bg-[#4ADE80] text-black hover:bg-[#22C55E]"
            >
              {signing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Sign BOL
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BOLPage;
